import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { Wallet, Warning, Plus, Trash } from "@phosphor-icons/react";

const fmt = (n, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(n || 0);

export default function ProjectBudget({ projectId, project, onProjectUpdate }) {
  const { can } = useCapabilities();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLine, setShowLine] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [line, setLine] = useState({ type: "custom", amount: "", description: "", spent_at: "" });
  const [settings, setSettings] = useState({
    budget_amount: project?.budget_amount || "",
    budget_currency: project?.budget_currency || "USD",
    role_rates: project?.role_rates || {},
    budget_thresholds: (project?.budget_thresholds || [80, 100]).join(","),
  });

  const canView = can("view_project_budget");
  const canManage = can("manage_project_budget");

  const load = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/budget`);
      setBudget(r.data);
    } catch (e) {
      setBudget(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const addLine = async () => {
    if (!line.amount) return;
    await api.post(`/projects/${projectId}/budget/lines`, {
      type: line.type,
      amount: parseFloat(line.amount),
      description: line.description,
      spent_at: line.spent_at || null,
    });
    setLine({ type: "custom", amount: "", description: "", spent_at: "" });
    setShowLine(false);
    load();
  };

  const delLine = async (lid) => {
    await api.delete(`/projects/${projectId}/budget/lines/${lid}`);
    load();
  };

  const saveSettings = async () => {
    const patch = {
      budget_amount: settings.budget_amount ? parseFloat(settings.budget_amount) : 0,
      budget_currency: settings.budget_currency,
      role_rates: Object.fromEntries(Object.entries(settings.role_rates || {}).map(([k, v]) => [k, parseFloat(v) || 0])),
      budget_thresholds: settings.budget_thresholds.split(",").map(x => parseInt(x.trim())).filter(x => !isNaN(x) && x > 0),
    };
    const r = await api.patch(`/projects/${projectId}`, patch);
    setShowSettings(false);
    onProjectUpdate?.(r.data);
    load();
  };

  if (!canView) {
    return <div className="p-8 text-center text-muted-foreground text-sm">You don't have permission to view budget.</div>;
  }
  if (loading) return <div className="p-8 text-center text-muted-foreground text-sm">Loading budget…</div>;
  if (!budget) return <div className="p-8 text-center text-muted-foreground text-sm">No budget data.</div>;

  const cur = budget.currency;
  const pct = Math.min(100, budget.consumption_pct);
  const barColor = budget.consumption_pct >= 100 ? "#FF3B30" : budget.consumption_pct >= 80 ? "#F59E0B" : "#22C55E";

  return (
    <div>
      {/* Header + settings */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><Wallet size={14} weight="bold" /> Project Budget</div>
          <div className="font-display text-2xl font-black">{fmt(budget.planned, cur)}</div>
          <div className="text-sm text-muted-foreground mt-1">Planned budget · {cur}</div>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <>
              <button className="border border-border px-4 py-2 text-sm hover:bg-muted" onClick={() => setShowSettings(true)} data-testid="budget-settings-btn">
                Budget Settings
              </button>
              <button className="btn-primary flex items-center gap-1" onClick={() => setShowLine(true)} data-testid="add-budget-line-btn">
                <Plus size={14} weight="bold" /> Add Line
              </button>
            </>
          )}
        </div>
      </div>

      {/* Alerts */}
      {budget.alerts?.length > 0 && (
        <div className="border border-[#FF3B30] bg-[#FFF5F5] p-4 mb-6 flex items-start gap-3" data-testid="budget-alerts">
          <Warning size={20} weight="fill" className="text-[#FF3B30] shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm text-[#FF3B30]">Budget alert</div>
            {budget.alerts.map((a, i) => (
              <div key={i} className="text-xs text-[#7F1D1D] mt-1">{a.message}</div>
            ))}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-border mb-6">
        <div className="grid-cell p-4">
          <div className="eyebrow mb-1">Consumed</div>
          <div className="font-display text-2xl font-black" data-testid="budget-consumed">{fmt(budget.consumed, cur)}</div>
        </div>
        <div className="grid-cell p-4">
          <div className="eyebrow mb-1">Remaining</div>
          <div className="font-display text-2xl font-black" style={{ color: budget.remaining < 0 ? "#FF3B30" : "#0A0A0A" }} data-testid="budget-remaining">
            {fmt(budget.remaining, cur)}
          </div>
        </div>
        <div className="grid-cell p-4">
          <div className="eyebrow mb-1">Consumption</div>
          <div className="font-display text-2xl font-black" style={{ color: barColor }} data-testid="budget-pct">{budget.consumption_pct}%</div>
        </div>
        <div className="grid-cell p-4">
          <div className="eyebrow mb-1">Thresholds</div>
          <div className="font-mono-mini text-sm mt-2">{(budget.thresholds || []).join("% / ")}%</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="w-full h-3 bg-muted overflow-hidden">
          <div className="h-3 transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <BreakdownBlock title="Labor" total={budget.breakdown.labor.total} cur={cur} lines={budget.breakdown.labor.lines}
          render={(l, i) => (
            <div key={i} className="text-xs flex items-center justify-between py-1.5 border-b border-border/60">
              <div>
                <div className="font-medium">{l.user_name}</div>
                <div className="text-muted-foreground font-mono-mini text-[10px]">
                  {l.hours}h × {fmt(l.hourly_rate, cur)} ({l.role || "no rate"})
                </div>
              </div>
              <div className="font-mono-mini">{fmt(l.cost, cur)}</div>
            </div>
          )} />
        <BreakdownBlock title="Expenses" total={budget.breakdown.expenses.total} cur={cur} lines={budget.breakdown.expenses.lines}
          render={(l) => (
            <div key={l.id} className="text-xs flex items-center justify-between py-1.5 border-b border-border/60">
              <div>
                <div className="font-medium capitalize">{l.category}</div>
                <div className="text-muted-foreground font-mono-mini text-[10px]">{l.vendor || l.status} · {l.date}</div>
              </div>
              <div className="font-mono-mini">{fmt(l.amount, cur)}</div>
            </div>
          )} />
        <BreakdownBlock title="Manual Lines" total={budget.breakdown.manual.total} cur={cur} lines={budget.breakdown.manual.lines}
          render={(l) => (
            <div key={l.id} className="text-xs flex items-center justify-between py-1.5 border-b border-border/60 group">
              <div className="min-w-0">
                <div className="font-medium capitalize truncate">{l.description || l.type}</div>
                <div className="text-muted-foreground font-mono-mini text-[10px]">{l.type} · {l.spent_at || l.created_at?.slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="font-mono-mini">{fmt(l.amount, cur)}</div>
                {canManage && (
                  <button onClick={() => delLine(l.id)} className="opacity-0 group-hover:opacity-100 text-[#FF3B30]" data-testid={`del-line-${l.id}`}>
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          )} />
      </div>

      {/* Add line modal */}
      {showLine && (
        <Modal onClose={() => setShowLine(false)} title="Add Budget Line">
          <div className="space-y-3">
            <select className="w-full border border-border px-3 py-2 text-sm" value={line.type} onChange={e => setLine({ ...line, type: e.target.value })}>
              <option value="custom">Custom</option>
              <option value="labor">Labor</option>
              <option value="expense">Expense</option>
            </select>
            <input type="number" placeholder="Amount" className="w-full border border-border px-3 py-2 text-sm"
              value={line.amount} onChange={e => setLine({ ...line, amount: e.target.value })} data-testid="line-amount" />
            <input type="text" placeholder="Description" className="w-full border border-border px-3 py-2 text-sm"
              value={line.description} onChange={e => setLine({ ...line, description: e.target.value })} data-testid="line-desc" />
            <input type="date" className="w-full border border-border px-3 py-2 text-sm"
              value={line.spent_at} onChange={e => setLine({ ...line, spent_at: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button className="border border-border px-4 py-2 text-sm" onClick={() => setShowLine(false)}>Cancel</button>
              <button className="btn-primary" onClick={addLine} data-testid="save-line">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Settings modal */}
      {showSettings && (
        <Modal onClose={() => setShowSettings(false)} title="Budget Settings">
          <div className="space-y-3">
            <label className="block text-xs font-mono-mini uppercase">Budget Amount</label>
            <input type="number" className="w-full border border-border px-3 py-2 text-sm"
              value={settings.budget_amount} onChange={e => setSettings({ ...settings, budget_amount: e.target.value })} data-testid="settings-amount" />
            <label className="block text-xs font-mono-mini uppercase">Currency</label>
            <input type="text" className="w-full border border-border px-3 py-2 text-sm"
              value={settings.budget_currency} onChange={e => setSettings({ ...settings, budget_currency: e.target.value.toUpperCase() })} />
            <label className="block text-xs font-mono-mini uppercase">Alert Thresholds (%, comma-sep)</label>
            <input type="text" className="w-full border border-border px-3 py-2 text-sm"
              value={settings.budget_thresholds} onChange={e => setSettings({ ...settings, budget_thresholds: e.target.value })} placeholder="80, 100" />
            <label className="block text-xs font-mono-mini uppercase">Role Rates (hourly)</label>
            {["admin", "manager", "hr", "employee"].map(rk => (
              <div key={rk} className="flex items-center gap-2">
                <span className="text-xs font-mono-mini w-24 uppercase">{rk}</span>
                <input type="number" className="flex-1 border border-border px-3 py-2 text-sm"
                  value={settings.role_rates[rk] || ""}
                  onChange={e => setSettings({ ...settings, role_rates: { ...settings.role_rates, [rk]: e.target.value } })}
                  placeholder="0" data-testid={`rate-${rk}`} />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button className="border border-border px-4 py-2 text-sm" onClick={() => setShowSettings(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveSettings} data-testid="save-budget-settings">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BreakdownBlock({ title, total, cur, lines, render }) {
  return (
    <div className="border border-border p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{title}</div>
        <div className="font-mono-mini text-sm font-semibold">{new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(total || 0)}</div>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {lines.length === 0 ? <div className="text-xs text-muted-foreground italic py-2">No entries.</div> : lines.map(render)}
      </div>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white border border-border p-6 w-[420px] max-w-[92vw]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-black">{title}</div>
          <button onClick={onClose} className="text-lg">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
